import React, {useCallback, useState} from 'react';
import createAxiosInstance from '../support/AxiosSupport';
import {formatDate} from '../utils/dateTime';
import PlanBadge from './PlanBadge';
import {invalidateSubscriptionCache} from '../service/subscriptionCache';

interface QuotaUsageDto {
    quotaType: string;
    used: number;
    limit: number;
    remaining: number;
    periodStart: string;
    periodEnd: string;
}

interface SubscriptionDto {
    id: string;
    ownerId: string;
    planCode: string;
    planName: string;
    status: string;
    startedAt: string;
    expiresAt: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    maxWardrobes: number;
    maxFamilyMembers: number;
    quotas: QuotaUsageDto[];
}

const PLANS = ['FREE', 'PRO', 'FAMILY'] as const;
type PlanCode = typeof PLANS[number];

const fmt = (d: string | null) => formatDate(d);

const AdminSubscriptions: React.FC = () => {
    const [ownerIdInput, setOwnerIdInput] = useState('');
    const [subscription, setSubscription] = useState<SubscriptionDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const [selectedPlan, setSelectedPlan] = useState<PlanCode | null>(null);
    const [changing, setChanging] = useState(false);
    const [changeError, setChangeError] = useState<string | null>(null);
    const [changeSuccess, setChangeSuccess] = useState(false);

    const fetchSubscription = useCallback(async () => {
        const id = ownerIdInput.trim();
        if (!id) return;
        setLoading(true);
        setFetchError(null);
        setSubscription(null);
        setSelectedPlan(null);
        setChangeSuccess(false);
        setChangeError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<SubscriptionDto>(`/admin/subscriptions/${id}`);
            setSubscription(res.data);
            setSelectedPlan(res.data.planCode as PlanCode);
        } catch (e: unknown) {
            const axErr = e as {response?: {data?: {message?: string}; status?: number}};
            if (axErr?.response?.status === 404) {
                setFetchError('Пользователь не найден или находится в другом тенанте');
            } else {
                setFetchError(axErr?.response?.data?.message || 'Не удалось загрузить подписку');
            }
        } finally {
            setLoading(false);
        }
    }, [ownerIdInput]);

    const handleChangePlan = async () => {
        if (!subscription || !selectedPlan || selectedPlan === subscription.planCode) return;
        setChanging(true);
        setChangeError(null);
        setChangeSuccess(false);
        try {
            const api = await createAxiosInstance();
            const res = await api.put<SubscriptionDto>(
                `/admin/subscriptions/${subscription.ownerId}/plan`,
                {planCode: selectedPlan},
            );
            setSubscription(res.data);
            setSelectedPlan(res.data.planCode as PlanCode);
            setChangeSuccess(true);
            // Администратор мог сменить тариф самому себе: сбрасываем общий кеш, иначе
            // бейдж в шапке остался бы с прежним тарифом до перезагрузки вкладки.
            invalidateSubscriptionCache();
        } catch (e: unknown) {
            const axErr = e as {response?: {data?: {message?: string}}};
            setChangeError(axErr?.response?.data?.message || 'Не удалось изменить план');
        } finally {
            setChanging(false);
        }
    };

    const planChanged = selectedPlan && subscription && selectedPlan !== subscription.planCode;

    return (
        <div className="space-y-6 max-w-2xl">
            {/* Search */}
            <div className="card p-6">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    Подписка пользователя
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Введите UUID пользователя (ownerId из JWT-токена), чтобы просмотреть и изменить его тариф.
                </p>
                <div className="flex gap-3">
                    <input
                        type="text"
                        value={ownerIdInput}
                        onChange={e => setOwnerIdInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && fetchSubscription()}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        className="input flex-1 font-mono text-sm"
                    />
                    <button
                        onClick={fetchSubscription}
                        disabled={loading || !ownerIdInput.trim()}
                        className="btn-primary px-5 min-w-[90px] flex items-center justify-center disabled:opacity-50"
                    >
                        {loading
                            ? <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full"/>
                            : 'Найти'
                        }
                    </button>
                </div>
                {fetchError && (
                    <p className="mt-3 text-sm text-red-600 dark:text-red-400">{fetchError}</p>
                )}
            </div>

            {/* Subscription card */}
            {subscription && (
                <div className="card p-6 space-y-5">
                    {/* Plan badge + status + ownerId */}
                    <div className="flex items-start justify-between flex-wrap gap-3">
                        <div className="space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                                <PlanBadge
                                    planCode={subscription.planCode}
                                    planName={subscription.planName}
                                />
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    subscription.status === 'ACTIVE'
                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                        : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                }`}>
                                    {subscription.status}
                                </span>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                                {subscription.ownerId}
                            </p>
                        </div>
                        <div className="text-right text-sm text-gray-600 dark:text-gray-400 space-y-0.5">
                            <div>Начало: <span className="font-medium text-gray-800 dark:text-gray-200">{fmt(subscription.startedAt)}</span></div>
                            <div>Истекает: <span className="font-medium text-gray-800 dark:text-gray-200">{fmt(subscription.expiresAt)}</span></div>
                        </div>
                    </div>

                    {/* Limits grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                            {label: 'Гардеробы', value: subscription.maxWardrobes},
                            {label: 'Участники', value: subscription.maxFamilyMembers},
                            {label: 'Период с', value: fmt(subscription.currentPeriodStart)},
                            {label: 'Период до', value: fmt(subscription.currentPeriodEnd)},
                        ].map(({label, value}) => (
                            <div key={label} className="bg-gray-50 dark:bg-white/5 rounded-xl p-3">
                                <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
                                <div className="mt-0.5 font-semibold text-gray-900 dark:text-gray-100 text-sm">{value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Quotas */}
                    {subscription.quotas.length > 0 && (
                        <div className="space-y-3">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Квоты</div>
                            {subscription.quotas.map(q => {
                                const unlimited = q.limit === -1;
                                const pct = unlimited ? 0 : Math.min(100, Math.round((q.used / q.limit) * 100));
                                return (
                                    <div key={q.quotaType}>
                                        <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                                            <span className="font-mono">{q.quotaType}</span>
                                            <span>
                                                {q.used} / {unlimited ? '∞' : q.limit}
                                                {!unlimited && (
                                                    <span className="ml-2 text-gray-400 dark:text-gray-500">
                                                        (осталось: {q.remaining})
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        {!unlimited && (
                                            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-[width] duration-modal ease-out ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-brand-500'}`}
                                                    style={{width: `${pct}%`}}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="border-t border-gray-100 dark:border-white/5 pt-5 space-y-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Изменить тариф</div>
                        <div className="flex gap-2 flex-wrap">
                            {PLANS.map(plan => (
                                <button
                                    key={plan}
                                    onClick={() => { setSelectedPlan(plan); setChangeSuccess(false); setChangeError(null); }}
                                    className={`px-5 py-2 rounded-xl text-sm font-medium border-2 pressable ${
                                        selectedPlan === plan
                                            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300'
                                            : 'border-transparent bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-white/20'
                                    }`}
                                >
                                    {plan}
                                    {subscription.planCode === plan && (
                                        <span className="ml-1.5 text-xs opacity-50">• текущий</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                            <button
                                onClick={handleChangePlan}
                                disabled={changing || !planChanged}
                                className="btn-primary px-6 min-w-[110px] flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {changing && (
                                    <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full"/>
                                )}
                                {changing ? 'Сохранение...' : 'Применить'}
                            </button>
                            {changeSuccess && (
                                <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                                    ✓ Тариф изменён на <strong>{subscription.planCode}</strong>
                                </span>
                            )}
                            {changeError && (
                                <span className="text-sm text-red-600 dark:text-red-400">{changeError}</span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminSubscriptions;
